import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { CreateEntityModal, SchemaField, CalcBreakdownPanel, LastUpdated, EntityGroupsPanel } from '../organisms';
import { EntityHeader } from '../shell';
import { DetailFieldGrid, singularize } from './detailFields';
import { tableToEntity } from '../lib/tableEntity';
import { groupableTypeForTable } from '../lib/groups';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * GenericDetail — schema-driven detail page for any entity table.
 *
 * Auto-renders all visible non-hidden fields as InfoCard rows grouped
 * by `field.section` (shared DetailFieldGrid). Edit button opens
 * CreateEntityModal in edit mode. Matches the v1 SPA openDetail() generic
 * pattern.
 *
 * Government reference entities (assessors / collectors / jurisdictions) use
 * the bespoke tabbed GovernmentEntityDetail page instead — they no longer
 * route through here, so this page carries no Contacts panel.
 *
 * Phase 11 Block 9 PR 2 (2026-05-12).
 */

export interface GenericDetailConfig {
  table: string;
  routePath?: string;
  apiPath?: string;
  title: string;                                      // base title — actual title pulls from row.name / row.account_number / row.id
  listPath?: string;                                  // default: /<routePath>
  disableEdit?: boolean;                              // hide Edit button (audit_logs append-only; users via invitation flow)
}

export function GenericDetailByConfig(config: GenericDetailConfig) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  // Gate Edit on the resource's can_edit (mirrors the server) so a view-only role
  // (training) / agent doesn't see an edit control whose PUT is rejected. Unmapped
  // tables → ungated (backward compatible). Hook — always runs, before any return.
  const detailEntity = tableToEntity(config.table);
  const canEditEntity = useEntityPermission(detailEntity ?? '__ungated__', 'can_edit');
  const canEdit = !detailEntity || canEditEntity;

  const apiPath = (config.apiPath ?? config.table).replace(/_/g, '-');
  const routePath = (config.routePath ?? config.table).replace(/_/g, '-');
  const listPath = config.listPath ?? `/${routePath}`;

  const schemaQuery = useQuery<{ fields: SchemaField[] }>({
    queryKey: ['schema', config.table],
    queryFn: ({ signal }) => api.get(`/api/schema/${config.table}`, signal),
    staleTime: 5 * 60_000,
  });

  const recordQuery = useQuery<Record<string, unknown>>({
    queryKey: [config.table, id],
    queryFn: ({ signal }) => api.get(`/api/${apiPath}/${id}`, signal),
    enabled: !!id,
  });

  if (schemaQuery.isLoading || recordQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading {config.title.toLowerCase()}…</p>;
  }

  const error = schemaQuery.error || recordQuery.error;
  if (error) {
    const notFound = error instanceof ApiClientError && error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>
          {notFound ? `${config.title} not found` : `Failed to load ${config.title.toLowerCase()}`}
        </h2>
        <p style={{ margin: 0 }}>{(error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate(listPath)}>
            Back to {config.title}
          </Button>
        </p>
      </div>
    );
  }

  if (!schemaQuery.data || !recordQuery.data) return null;

  const row = recordQuery.data;

  // Derive a display title: prefer `name`, then `account_number`, then a sliced UUID
  const title =
    (typeof row.name === 'string' && row.name)
    || (typeof row.account_number === 'string' && row.account_number)
    || (typeof row.id === 'string' && row.id.slice(0, 8))
    || config.title;

  return (
    <>
      <EntityHeader
        prefix={singularize(config.title)}
        title={String(title)}
        subtitle={<LastUpdated updatedBy={row.updated_by as string | null | undefined} updatedAt={row.updated_at as string | null | undefined} />}
        statusPills={
          typeof row.status === 'string' ? (
            <Badge variant="neutral">{row.status.replace(/_/g, ' ')}</Badge>
          ) : undefined
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate(listPath)}>
              Back
            </Button>
            {!config.disableEdit && canEdit && (
              <Button variant="primary" size="sm" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
          </>
        }
      />

      <DetailFieldGrid fields={schemaQuery.data.fields} row={row} />

      {id && config.table === 'tax_records' && (
        <section style={{ marginTop: 'var(--sp-6)' }}>
          <CalcBreakdownPanel taxRecordId={id} />
        </section>
      )}

      {/* Universal Groups (spec 0008): a "Groups" section for any groupable internal
          record. GenericDetail is flat (not tabbed), so it renders as a section; the
          bespoke tabbed pages render it as a Groups tab. Absent for non-groupable
          tables (audit_logs, users) and hidden for a viewer without the `group` grant. */}
      {id && groupableTypeForTable(config.table) && (
        <section style={{ marginTop: 'var(--sp-6)' }}>
          <EntityGroupsPanel entityType={groupableTypeForTable(config.table) as string} entityId={id} />
        </section>
      )}

      {id && !config.disableEdit && canEdit && (
        <CreateEntityModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          table={config.table}
          routePath={apiPath}
          recordId={id}
          invalidateKeys={[[`${apiPath}-list`], [config.table, id]]}
        />
      )}
    </>
  );
}

